/**
 * O contrato da API, do ponto de vista deste app.
 *
 * Escrito à mão de propósito, e cobrindo só o que o app do CLIENTE consome. A
 * alternativa seria gerar tudo a partir do OpenAPI, o que traria junto as
 * centenas de campos do painel administrativo e do painel master — um contrato
 * grande demais para revisar, no qual uma mudança de verdade passa despercebida.
 *
 * Onde o servidor devolve dinheiro, o campo é SEMPRE em centavos e inteiro.
 * Onde devolve instante, é ISO-8601 em UTC; a conversão para o fuso da
 * barbearia acontece na exibição, nunca no armazenamento.
 */

export interface Paginated<T> {
  readonly data: readonly T[];
  readonly meta: {
    readonly page: number;
    readonly pageSize: number;
    readonly total: number;
    readonly totalPages: number;
    readonly hasNext: boolean;
    readonly hasPrevious: boolean;
  };
}

// ============================================================ barbearia ====

/**
 * Expediente da barbearia.
 *
 * O horário vem como "HH:mm", e não em minutos: é assim que a API o publica,
 * porque é assim que uma pessoa o escreve. O banco é que guarda minutos desde
 * a meia-noite.
 */
export interface TenantBusinessHour {
  readonly id?: string;
  readonly weekday: number;
  readonly opensAt: string;
  readonly closesAt: string;
}

export interface PublicTenant {
  readonly slug: string;
  readonly name: string;
  readonly timezone: string;
  readonly currency: string;
  readonly locale: string;
  readonly branding: {
    readonly logoUrl: string | null;
    readonly faviconUrl: string | null;
    readonly coverUrl: string | null;
    readonly primaryColor: string;
    readonly secondaryColor: string;
  };
  readonly contact: {
    readonly phone: string | null;
    readonly whatsapp: string | null;
    readonly email: string | null;
    readonly instagram: string | null;
  };
  readonly address: {
    readonly line: string | null;
    readonly district: string | null;
    readonly city: string | null;
    readonly state: string | null;
    readonly zipCode: string | null;
    readonly latitude: number | null;
    readonly longitude: number | null;
  };
  readonly booking: {
    readonly slotStepMinutes: number;
    readonly minLeadMinutes: number;
    readonly maxAdvanceDays: number;
    readonly cancellationDeadlineHours: number;
    readonly allowCustomerReschedule: boolean;
    /** O app só oferece "repetir toda semana" quando o dono liberou. */
    readonly allowCustomerRecurrence: boolean;
    /** Pagar adiantado por PIX com desconto: plataforma com PIX e desconto ligado. */
    readonly pix?: { readonly available: boolean; readonly discountPercent: number };
  };
  readonly businessHours: readonly TenantBusinessHour[];
}

// ========================================================= autenticação ====

export interface AuthUser {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly phoneE164?: string;
  readonly tenantId?: string | null;
}

export interface AuthTokens {
  readonly accessToken: string;
  readonly expiresIn: number;
  readonly user: AuthUser;
}

export interface OtpRequestResult {
  readonly sent: boolean;
  readonly channel: string;
  readonly maskedPhone: string;
  readonly expiresInSeconds: number;
  /** Só em desenvolvimento — o servidor recusa expor isto em produção. */
  readonly devCode?: string;
}

export interface RegistrationRequired {
  readonly registrationRequired: true;
  readonly signupToken: string;
}

export type VerifyOtpResult = AuthTokens | RegistrationRequired;

export function isRegistrationRequired(result: VerifyOtpResult): result is RegistrationRequired {
  return 'registrationRequired' in result;
}

// =============================================================== catálogo ==

export interface PublicService {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly priceCents: number;
  readonly durationMinutes: number;
  readonly imageUrl: string | null;
  /** Encaixe: não é escolhido como serviço; entra junto com outro. */
  readonly isAddOn?: boolean;
}

export interface PublicBarber {
  readonly id: string;
  readonly name: string;
  readonly photoUrl: string | null;
  readonly bio: string | null;
  readonly specialties: readonly string[];
  /** Serviços que o profissional faz — o app usa para oferecer encaixes. */
  readonly serviceIds?: readonly string[];
}

export interface PublicPlanBenefit {
  readonly id: string;
  readonly type: string;
  readonly label: string;
  readonly quantity: number | null;
  readonly percentOff: number | null;
  readonly amountOffCents: number | null;
  readonly appliesToAllServices: boolean;
  readonly service: { readonly id: string; readonly name: string } | null;
}

export interface PublicPlan {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly priceCents: number;
  readonly highlight: boolean;
  readonly displayOrder: number;
  readonly benefits: readonly PublicPlanBenefit[];
}

// ============================================================== agenda ====

export interface AvailabilitySlot {
  readonly startsAt: string;
  readonly endsAt: string;
  /** Já no fuso da barbearia — o app não converte nada aqui. */
  readonly label: string;
  readonly barberIds: readonly string[];
}

export interface Availability {
  readonly date: string;
  readonly timezone: string;
  readonly serviceId: string;
  readonly durationMinutes: number;
  readonly slots: readonly AvailabilitySlot[];
}

export type AppointmentStatusValue =
  'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

export interface Appointment {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: AppointmentStatusValue;
  readonly priceCents: number;
  readonly discountCents: number;
  readonly notes: string | null;
  readonly cancelReason: string | null;
  /** Preenchido quando a semana faz parte de um horário fixo. */
  readonly recurrenceId?: string | null;
  /** Serviços marcados juntos, em sequência, compartilham este id. */
  readonly groupId?: string | null;
  /** Encaixes pendurados no horário, com o preço combinado. */
  readonly addOns?: readonly AppointmentAddOn[];
  /** Soma dos encaixes. Total do horário: `appointmentTotalCents`. */
  readonly addOnsCents?: number;
  /** Já pago adiantado (PIX ou crédito). Falta: `appointmentDueCents`. */
  readonly paidCents?: number;
  readonly customer: { readonly id: string; readonly name: string };
  readonly barber: { readonly id: string; readonly name: string; readonly photoUrl: string | null };
  readonly service: {
    readonly id: string;
    readonly name: string;
    readonly durationMinutes: number;
    readonly priceCents: number;
  };
}

// ======================================================== horário fixo ====

export type RecurrenceStatusValue = 'PENDING' | 'ACTIVE' | 'CANCELLED';

/** Horário fixo semanal: "toda segunda às 10h com o Rafael". */
export interface Recurrence {
  readonly id: string;
  /** 0 = domingo. */
  readonly weekday: number;
  /** Minutos desde a meia-noite, no fuso da barbearia. */
  readonly startMinute: number;
  readonly occupiedMinutes: number;
  /** Data local (AAAA-MM-DD) da primeira semana. */
  readonly startsOn: string;
  readonly status: RecurrenceStatusValue;
  readonly cancelReason: string | null;
  readonly customer: { readonly id: string; readonly name: string };
  readonly barber: { readonly id: string; readonly name: string };
  readonly service: { readonly id: string; readonly name: string };
  readonly nextOccurrences: readonly {
    readonly id: string;
    readonly startsAt: string;
    readonly status: AppointmentStatusValue;
  }[];
}

const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** "toda segunda às 10:00" — como o horário fixo aparece em qualquer tela. */
export function describeRecurrence(r: Pick<Recurrence, 'weekday' | 'startMinute'>): string {
  const h = String(Math.floor(r.startMinute / 60)).padStart(2, '0');
  const m = String(r.startMinute % 60).padStart(2, '0');
  // Domingo e sábado são masculinos: "todo sábado", "toda segunda".
  const todo = r.weekday === 0 || r.weekday === 6 ? 'todo' : 'toda';
  return `${todo} ${WEEKDAY_NAMES[r.weekday] ?? ''} às ${h}:${m}`;
}

export interface AppointmentAddOn {
  readonly serviceId: string;
  readonly priceCents: number;
  readonly service: { readonly id: string; readonly name: string };
}

/** 409 de horário tomado vem com sugestões — a UI as oferece em vez do erro. */
export interface SlotTakenDetails {
  readonly alternativas?: readonly { readonly startsAt: string; readonly label: string }[];
}

// ================================================================ plano ====

/** Uma cota do ciclo: quantas vezes o benefício ainda dá para usar. */
export interface QuotaBalance {
  readonly benefitId: string;
  readonly label: string;
  readonly serviceId: string | null;
  readonly serviceName: string | null;
  readonly total: number;
  readonly used: number;
  readonly remaining: number;
}

export interface PlanBalance {
  readonly subscription: {
    readonly id: string;
    readonly status: string;
    readonly planName: string;
    readonly currentPeriodEnd: string | null;
  } | null;
  readonly quotas: readonly QuotaBalance[];
  readonly discounts: ReadonlyArray<{ readonly label: string; readonly description: string }>;
}

export interface MySubscription {
  readonly subscription: {
    readonly id: string;
    readonly status: string;
    readonly priceCents: number;
    readonly startedAt: string | null;
    readonly currentPeriodStart: string | null;
    readonly currentPeriodEnd: string | null;
    readonly cancelledAt: string | null;
    readonly checkoutUrl: string | null;
    /** Cartão pelo Mercado Pago, ou PIX pago a cada ciclo. */
    readonly paymentMethod?: 'MERCADO_PAGO' | 'PIX';
    readonly plan: {
      readonly id: string;
      readonly name: string;
      readonly description: string | null;
      readonly priceCents: number;
      readonly benefits: readonly PublicPlanBenefit[];
    };
  } | null;
}

/**
 * Como o plano afeta UM serviço, antes de confirmar o agendamento.
 *
 * `coveredByPlan` é a pergunta que o cliente realmente faz: "isso está incluso
 * no meu plano?". Cota vem antes de desconto — se o plano cobre, sai de graça,
 * e não "com 10% off".
 */
export interface BenefitPreview {
  readonly serviceId: string;
  readonly serviceName: string;
  readonly coveredByPlan: boolean;
  readonly benefitLabel?: string;
  readonly used?: number;
  readonly of?: number;
  readonly remaining?: number;
  readonly discountLabel?: string;
  readonly discountCents?: number;
  readonly originalPriceCents: number;
  readonly finalPriceCents: number;
}

export interface CheckoutResult {
  readonly subscriptionId: string;
  readonly checkoutUrl: string;
}

// ============================================================== perfil ====

export interface CustomerProfile {
  readonly id: string;
  readonly name: string;
  readonly phoneE164: string;
  readonly email: string | null;
  readonly birthDate: string | null;
  readonly photoUrl: string | null;
  readonly noShowCount: number;
  /** CPF de quem paga o PIX, só mascarado (***.***.*89-01). */
  readonly cpfHint?: string | null;
  /** Crédito nesta barbearia: pagou e não foi atendido. Usado sozinho no próximo. */
  readonly creditCents?: number;
}

// ================================================================== PIX ====

export type PixChargeStatusValue = 'PENDING' | 'PAID' | 'EXPIRED' | 'LATE_PAID' | 'CANCELLED';

/** Cobrança PIX com desconto, como o app recebe para mostrar. */
export interface PixCharge {
  readonly chargeId: string;
  readonly status: PixChargeStatusValue;
  /** Antes do desconto (e já sem o crédito usado). */
  readonly baseCents: number;
  readonly discountCents: number;
  readonly amountCents: number;
  readonly expiresAt: string;
  readonly pixCopyPaste: string;
  /** Data URI da imagem do QR. */
  readonly qrCode: string;
}
